// heatmap/utils.js — small helpers used by the heatmap components + data hook.

const START_YEAR = 1992;
export const ALL_LABEL = "All";

// Season start year for a date (Date or ISO string): seasons start on
// August 1 UTC, so June/July belong to the season that's ending (the 2019/20
// COVID restart ran until July 26, 2020). Keep in sync with
// _season_start_year() in lambdas/pl-gami-image. Returns null for bad input.
export const seasonStartYear = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  const y = dt.getUTCFullYear();
  return dt.getUTCMonth() >= 7 ? y : y - 1;
};

export const getCurrentSeasonStartYear = () => seasonStartYear(new Date());

// "2023/2024" (a SeasonSelect value) -> "2023/24".
export const shortSeasonLabel = (label) => `${label.slice(0, 4)}/${label.slice(7)}`;
export const isCurrentSeason = (label) => parseInt(label.slice(0, 4), 10) === getCurrentSeasonStartYear();

export const buildSeasonOptions = () => {
  const end = getCurrentSeasonStartYear();
  const vals = [ALL_LABEL];
  for (let y = end; y >= START_YEAR; y--) vals.push(`${y}/${y + 1}`);
  return vals;
};

export const toSeasonId = (label) => {
  const startYear = parseInt(label.slice(0, 4), 10);
  const a = String(startYear % 100).padStart(2, "0");
  const b = String((startYear + 1) % 100).padStart(2, "0");
  return `${a}${b}`;
};

export const safeNum = (v) => {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export const parseScoreString = (scoreStr) => {
  if (!scoreStr || typeof scoreStr !== "string") return null;
  const parts = scoreStr.split("-");
  if (parts.length !== 2) return null;
  const a = safeNum(parts[0].trim());
  const b = safeNum(parts[1].trim());
  if (a === null || b === null) return null;
  return { a, b };
};

// -----------------------------------------------------------------------------
// Grid axes
// -----------------------------------------------------------------------------
// Cell keys are "x-y": home-away ("1-3" = away win) in HOME_AWAY mode,
// winner-first ("3-1") in WINNER_LOSER mode, where the grid shows each
// scoreline on both sides of the diagonal. The API and summary table are
// always keyed winner-first.
export const HOME_AWAY = "homeAway";
export const WINNER_LOSER = "winnerLoser";

export const AXIS_LABELS = {
  [HOME_AWAY]: { x: "Home Team Goals", y: "Away Team Goals", short: "Home – Away" },
  // Mirrored grid, read from either team's side: across is the goals it
  // scored, up is the goals it conceded, so 3–1 and 1–3 are the same result.
  [WINNER_LOSER]: { x: "Goals Scored", y: "Goals Conceded", short: "Winner – Loser" },
};

export const toWinnerFirst = (key) => {
  const p = parseScoreString(key);
  return p ? `${Math.max(p.a, p.b)}-${Math.min(p.a, p.b)}` : key;
};

// Map winner-first summary rows to cells keyed for the given axes. Each cell
// has the summary-row shape: { score, count, firstOccurrence, latestOccurrence }.
export const summaryCells = (rows, axes) => {
  const cells = {};
  for (const row of rows || []) {
    const p = parseScoreString(row.score);
    if (!p) continue;
    if (axes === WINNER_LOSER || p.a === p.b) {
      cells[row.score] = row;
      continue;
    }
    if (row.home) cells[`${p.a}-${p.b}`] = { score: `${p.a}-${p.b}`, ...row.home };
    if (row.away) cells[`${p.b}-${p.a}`] = { score: `${p.b}-${p.a}`, ...row.away };
  }
  return cells;
};

// Friendlier names for display; the data keeps football-data's shortName.
const DISPLAY_NAMES = {
  "Brighton Hove": "Brighton",
  Nottingham: "Nott'm Forest",
  Wolverhampton: "Wolves",
  "Wimbledon FC": "Wimbledon",
  "Oldham Athl": "Oldham",
};
export const displayName = (name) => DISPLAY_NAMES[name] || name;
const displayTeam = (team) => (team ? { ...team, name: displayName(team.name) } : team);

// Display order for an occurrence ({ highTeam, highScore, lowTeam, lowScore,
// highIsHome }): home side first in HOME_AWAY mode, winner first otherwise.
export const orientOccurrence = (occ, axes) => {
  const high = { team: occ.highTeam, score: occ.highScore };
  const low = { team: occ.lowTeam, score: occ.lowScore };
  const [left, right] =
    axes === HOME_AWAY && occ.highIsHome === false ? [low, high] : [high, low];
  return {
    leftTeam: displayTeam(left.team),
    leftScore: left.score,
    rightScore: right.score,
    rightTeam: displayTeam(right.team),
  };
};

export const occurrenceLine = (occ, axes) => {
  const o = orientOccurrence(occ, axes);
  return `${o.leftTeam?.name} ${o.leftScore}–${o.rightScore} ${o.rightTeam?.name}`;
};

export const isValidUtc = (utc) => {
  if (!utc || typeof utc !== "string") return false;
  const d = new Date(utc);
  return !isNaN(d.getTime());
};

// Format in UTC: most historical matches are stored date-only at midnight UTC,
// which a local-time format would show as the previous day in the Americas.
export const formatDate = (utcDate) =>
  isValidUtc(utcDate)
    ? new Date(utcDate).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      })
    : "—";

// -----------------------------------------------------------------------------
// Heatmap colour bands
// -----------------------------------------------------------------------------
// Cells are coloured in discrete bands so the legend maps exactly onto the
// grid. The colours sample one brand ramp, from deep teal (rare) through cyan
// and indigo to hot pink (common). "Once" (a scorigami) always gets its own band.
const RAMP = [
  { t: 0.0, c: [18, 52, 86] },
  { t: 0.18, c: [57, 208, 255] },
  { t: 0.45, c: [111, 93, 245] },
  { t: 0.72, c: [200, 59, 212] },
  { t: 1.0, c: [255, 77, 166] },
];

const rampRgb = (t) => {
  const x = Math.max(0, Math.min(1, t));
  for (let i = 0; i < RAMP.length - 1; i++) {
    const a = RAMP[i];
    const b = RAMP[i + 1];
    if (x <= b.t) {
      const k = (x - a.t) / (b.t - a.t);
      return a.c.map((v, j) => Math.round(v + (b.c[j] - v) * k));
    }
  }
  return RAMP[RAMP.length - 1].c;
};

export const rgb = (c) => `rgb(${c[0]},${c[1]},${c[2]})`;
export const EMPTY_CELL = "rgba(255,255,255,0.045)";

// All-time counts reach the thousands; single seasons top out in the tens.
const ALL_TIME_EDGES = [1, 2, 10, 50, 200, 1000];
const SEASON_EDGES = [1, 2, 3, 5, 10, 20];

// [{ min, max, label, color: [r, g, b] }], rarest first.
export const legendBands = (maxCount) => {
  const top = Math.max(1, maxCount || 1);
  const edges = (top > 100 ? ALL_TIME_EDGES : SEASON_EDGES).filter((e) => e <= top);
  return edges.map((min, i) => {
    const next = edges[i + 1];
    const max = next ? next - 1 : Infinity;
    let label;
    if (min === 1 && max === 1) label = "Once";
    else if (max === Infinity) label = `${min.toLocaleString()}+`;
    else if (min === max) label = String(min);
    else label = `${min.toLocaleString()}–${max.toLocaleString()}`;
    const t = edges.length === 1 ? 0.08 : 0.08 + (0.92 * i) / (edges.length - 1);
    return { min, max, label, color: rampRgb(t) };
  });
};

export const bandFor = (count, bands) =>
  bands.find((b) => count >= b.min && count <= b.max) || bands[bands.length - 1];

// One text colour for every band. Near-black clears 4:1 contrast on all of
// them (lowest on the teal "Once" band); white would drop to 2.5:1 on cyan.
export const CELL_TEXT = "rgba(10,10,14,0.9)";

// "1,355" when the cell has room, "1.4k" when it doesn't.
export const formatCellCount = (n, cellSize) =>
  n < 1000 || cellSize >= 50 ? n.toLocaleString() : `${(n / 1000).toFixed(1)}k`;
